import { PageTemplate } from '@/app/_common/page-template'
import { Button } from '@/components/ui/button'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import {
  useIndustryReferenceQuery,
  type IndustryReferenceItem,
} from '@/hooks/api/use-industry-reference-query'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { T } from '@tolgee/react'
import React from 'react'

export function IndustryReferenceTable() {
  const { data, error, isSuccess, isPending } = useIndustryReferenceQuery()
  const [sorting, setSorting] = React.useState<SortingState>([])

  const columns = React.useMemo<ColumnDef<IndustryReferenceItem>[]>(
    () => [
      {
        size: 50,
        accessorKey: 'ID',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='industryReference.table.headers.id'>
            <T keyName='industryReference.table.headers.id' />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => <div>{row.getValue('ID')}</div>,
      },
      {
        accessorKey: 'Reference/Framework',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            titleKey='industryReference.table.headers.framework'
          >
            <T keyName='industryReference.table.headers.framework' />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => {
          const framework = row.getValue('Reference/Framework') as string
          const referenceLink = row.original.referenceLink

          if (referenceLink) {
            return (
              <Button
                unstyled
                className='h-auto p-0 underline hover:text-neutral-300'
                onClick={() => window.open(referenceLink, '_blank', 'noopener,noreferrer')}
              >
                {framework}
              </Button>
            )
          }

          return <div>{framework}</div>
        },
      },
      {
        accessorKey: 'Industry',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            titleKey='industryReference.table.headers.industry'
          >
            <T keyName='industryReference.table.headers.industry' />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => {
          const industry = row.getValue('Industry') as string
          return <div>{industry}</div>
        },
      },
      {
        size: 400,
        accessorKey: 'Description',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            titleKey='industryReference.table.headers.description'
          >
            <T keyName='industryReference.table.headers.description' />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => <div>{row.getValue('Description')}</div>,
      },
      {
        size: 400,
        accessorKey: "Where it's used",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='industryReference.table.headers.usage'>
            <T keyName='industryReference.table.headers.usage' />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => <div>{row.getValue("Where it's used")}</div>,
      },
    ],
    [],
  )

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    sortDescFirst: true,
    enableColumnFilters: false,
    state: {
      sorting,
    },
  })

  const { rows } = table.getRowModel()
  const isInitialLoading = isPending
  const isTableLoading = isInitialLoading && rows.length === 0

  return (
    <div className='flex w-full flex-col gap-6'>
      <PageTemplate.Header>
        <div>
          <PageTemplate.H4 className='font-normal text-neutral-500'>
            <T keyName='industryReference.title' />
          </PageTemplate.H4>
        </div>
      </PageTemplate.Header>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: `${header.getSize()}px` }}
                  data-state={
                    (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isTableLoading && <TableSkeletonRows rowCount={3} cellCount={columns.length} />}

          {error && <TableErrorRow colSpan={columns.length} />}

          {isSuccess &&
            rows.length > 0 &&
            rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className='h-[72px]'
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}px` }}
                    data-state={
                      (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {isSuccess && rows.length === 0 && !isPending && (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center'>
                <T keyName='industryReference.table.noResults' />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
